import { Checkbox as RadixCheckbox, useThemeContext } from '@radix-ui/themes';
import { Flex } from '@/components/ui';
import { forwardRef, ReactNode, useId } from 'react';

import type { RadixCheckboxProps } from '@/types/radix-ui';
import clsx from 'clsx';

import { Label } from './Label';

export type CheckboxSize = 'small' | 'medium' | 'large';
export type CheckboxColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'destructive';
export type CheckboxVariant = 'classic' | 'surface' | 'soft';

interface BaseCheckboxProps {
  size?: CheckboxSize;
  color?: CheckboxColor;
  variant?: CheckboxVariant;
  className?: string;
  label?: ReactNode;
  highContrast?: boolean;
  id?: string;
}

export type CheckboxProps = Omit<
  RadixCheckboxProps,
  'size' | 'color' | 'variant'
> &
  BaseCheckboxProps;

const sizeMapping = {
  small: '1',
  medium: '2',
  large: '3',
} as const;

const variantMapping = {
  classic: 'classic',
  surface: 'surface',
  soft: 'soft',
} as const;

function resolveColor(
  color: CheckboxColor,
  accentColor?: string
): {
  color: RadixCheckboxProps['color'];
  highContrast?: boolean;
} {
  const allowedColors = [
    'gray',
    'blue',
    'red',
    'green',
    'teal',
    'ruby',
    'amber',
    'orange',
    'crimson',
    'jade',
    'pink',
    'plum',
    'purple',
    'violet',
    'indigo',
  ];
  switch (color) {
    case 'primary':
      return {
        color: allowedColors.includes(accentColor || '')
          ? (accentColor as RadixCheckboxProps['color'])
          : 'indigo',
        highContrast: true,
      };
    case 'secondary':
      return { color: 'gray', highContrast: true };
    case 'default':
      return { color: 'gray' };
    case 'success':
      return { color: 'jade' };
    case 'warning':
      return { color: 'amber' };
    case 'info':
      return { color: 'blue' };
    case 'error':
      return { color: 'crimson' };
    case 'destructive':
      return { color: 'ruby' };
    default:
      return { color: 'gray' };
  }
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      variant = 'classic',
      color = 'primary',
      size = 'medium',
      className,
      label,
      highContrast = true,
      id,
      ...props
    },
    ref
  ) => {
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const { color: resolvedColor, highContrast: resolvedContrast } =
      resolveColor(color, accentColor);
    const mappedSize = sizeMapping[size];
    const mappedVariant = variantMapping[variant];
    const generatedId = useId();
    const checkboxId = id || generatedId;
    if (label) {
      return (
        <Flex display="flex" direction="row" align="center" gap="2" asChild>
          <Label htmlFor={checkboxId}>
            <RadixCheckbox
              ref={ref}
              id={checkboxId}
              color={resolvedColor}
              variant={mappedVariant}
              size={mappedSize}
              highContrast={
                typeof highContrast === 'boolean'
                  ? highContrast
                  : resolvedContrast
              }
              className={clsx(
                'checkbox',
                `checkbox-size-${size}`,
                `checkbox-color-${color}`,
                `checkbox-variant-${variant}`,
                className
              )}
              {...props}
            />
            {label}
          </Label>
        </Flex>
      );
    }
    return (
      <Flex align="center" gap="2" asChild>
        <RadixCheckbox
          ref={ref}
          id={checkboxId}
          color={resolvedColor}
          variant={mappedVariant}
          size={mappedSize}
          highContrast={
            typeof highContrast === 'boolean' ? highContrast : resolvedContrast
          }
          className={clsx(
            'checkbox',
            `checkbox-size-${size}`,
            `checkbox-color-${color}`,
            `checkbox-variant-${variant}`,
            className
          )}
          {...props}
        />
      </Flex>
    );
  }
);
Checkbox.displayName = 'Checkbox';
